/**
 * PipelineCustomerPage — one customer's unbilled tickets (#50 PR A, plan §2b).
 *
 * Reads GET /v1/billing/pipeline/tickets for the customer in the URL (`ho:<id>` or
 * `op:<id>`, the Pipeline's own row key) and the month in `?month=`. By default it lists
 * what Generate would bill for that month; "All unbilled" adds the rest, marked, because
 * the Pipeline total counts every unbilled line and Generate bills only the month's.
 *
 * Read-only. Generate here is the Pipeline's own mutation. Money via formatUsdFromCents
 * (pre-tax: tax is applied at Generate); times local (DEC-004). The frontend gate is
 * UX-only — the API enforces VIEW_INVOICES / CREATE_INVOICE.
 */

import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ApiError } from '@lodex-pro/web-shared';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';

import { formatUsdFromCents } from '../lib/money';
import { formatDate } from '../lib/timestamp';
import { useUserTimezone } from '../lib/use-user-timezone';
import { periodFromMonthValue, useBillingMonth } from '../lib/billing-period';
import { parseCustomerKey } from '../lib/customer-key';
import {
  useGenerateInvoice,
  usePipelineTickets,
  type GenerateInvoiceResponse,
} from '../features/billing/hooks';
import { billingMethodLabel, periodMarker, ticketLabel } from '../features/billing/ticket-format';
import { formatNumber } from '@lodex-pro/shared/number-format';

const MONTH_INPUT_STYLE: React.CSSProperties = {
  height: '40px',
  borderRadius: '10px',
  border: '1px solid var(--color-border-secondary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  padding: '0 12px',
  fontSize: '14px',
};

export function PipelineCustomerPage(): React.JSX.Element {
  const params = useParams<{ customerKey: string }>();
  const navigate = useNavigate();
  const tz = useUserTimezone();
  const key = params.customerKey ?? '';
  const customer = parseCustomerKey(key);
  const [monthValue, setMonthValue] = useBillingMonth();
  const period = periodFromMonthValue(monthValue);
  const [includeAll, setIncludeAll] = React.useState(false);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePipelineTickets(customer, period, includeAll);

  const generate = useGenerateInvoice((result: GenerateInvoiceResponse) => {
    if (result.created) {
      toast.success('Invoice generated');
    } else {
      toast.info('Nothing to bill for this customer and month');
    }
  });

  const first = data?.pages[0];
  const tickets = data?.pages.flatMap((p) => p.tickets) ?? [];
  const notFound =
    customer === null || (isError && error instanceof ApiError && error.status === 404);
  const monthSearch = `?month=${monthValue}`;

  return (
    <div style={{ padding: '24px', maxWidth: '1100px' }}>
      <Button
        variant="ghost"
        onClick={(): void => {
          void navigate(`/${monthSearch}`);
        }}
        style={{ marginBottom: '16px' }}
      >
        <ArrowLeft size={16} aria-hidden="true" style={{ marginRight: '6px' }} />
        Back to Pipeline
      </Button>

      {notFound && (
        <p style={{ color: 'var(--color-text-secondary)' }}>
          This customer isn&apos;t in the Pipeline.
        </p>
      )}

      {!notFound && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
              marginBottom: '20px',
            }}
          >
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
                {first?.customer.operatorName ?? (isLoading ? '…' : 'Unnamed customer')}
              </h2>
              {first !== undefined && (
                <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                  This month: {formatUsdFromCents(first.period.totalCents)} (
                  {formatNumber(first.period.ticketCount)}{' '}
                  {first.period.ticketCount === 1 ? 'ticket' : 'tickets'}){' · '}All unbilled:{' '}
                  {formatUsdFromCents(first.allUnbilled.totalCents)}
                  {' · '}Pre-tax
                </p>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <label
                htmlFor="billing-month"
                style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}
              >
                Billing period
              </label>
              <input
                id="billing-month"
                type="month"
                value={monthValue}
                onChange={(e): void => {
                  setMonthValue(e.target.value);
                }}
                className="[color-scheme:dark]"
                style={MONTH_INPUT_STYLE}
              />
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}
              >
                <input
                  type="checkbox"
                  checked={includeAll}
                  onChange={(e): void => {
                    setIncludeAll(e.target.checked);
                  }}
                />
                All unbilled
              </label>
              {customer !== null && (
                <Button
                  disabled={generate.isPending || period === null}
                  onClick={(): void => {
                    if (period === null) return;
                    generate.mutate(
                      { ...customer, periodStart: period.periodStart, periodEnd: period.periodEnd },
                      {
                        onError: () => {
                          toast.error('Could not generate the invoice. Try again.');
                        },
                      },
                    );
                  }}
                >
                  {generate.isPending && (
                    <Loader2
                      size={14}
                      aria-hidden="true"
                      style={{ marginRight: '6px' }}
                      className="animate-spin"
                    />
                  )}
                  Generate invoice
                </Button>
              )}
            </div>
          </div>

          {isLoading && (
            <p role="status" style={{ color: 'var(--color-text-secondary)' }}>
              Loading tickets…
            </p>
          )}

          {isError && (
            <div style={{ color: 'var(--color-danger)' }}>
              <p style={{ marginBottom: '8px' }}>
                Couldn&apos;t load this customer&apos;s tickets.
              </p>
              <Button
                variant="outline"
                onClick={(): void => {
                  void refetch();
                }}
              >
                Retry
              </Button>
            </div>
          )}

          {first !== undefined && tickets.length === 0 && first.otherCharges.eventCount === 0 && (
            <p style={{ color: 'var(--color-text-secondary)' }}>
              {includeAll
                ? 'No unbilled tickets for this customer.'
                : 'Nothing for this customer bills in this month. Turn on “All unbilled” to see the rest.'}
            </p>
          )}

          {first !== undefined && (tickets.length > 0 || first.otherCharges.eventCount > 0) && (
            <div
              style={{
                borderRadius: 'var(--radius-card)',
                backgroundColor: 'var(--color-bg-secondary)',
                overflow: 'auto',
              }}
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticket</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Job type</TableHead>
                    <TableHead className="text-right">Volume (bbl)</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead className="text-right">Extras</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((t) => {
                    const marker = includeAll ? periodMarker(t) : null;
                    return (
                      <TableRow key={t.jobId}>
                        <TableCell>
                          <Link
                            to={`/pipeline/${key}/tickets/${t.jobId}${monthSearch}`}
                            style={{ color: 'var(--color-link)', fontWeight: 500 }}
                          >
                            {ticketLabel(t.haulId, t.jobId)}
                          </Link>
                          {t.edited && (
                            <Badge variant="secondary" style={{ marginLeft: '8px' }}>
                              Edited
                            </Badge>
                          )}
                          {marker !== null && (
                            <Badge variant="outline" style={{ marginLeft: '8px' }}>
                              {marker === 'OUTSIDE'
                                ? 'Outside this month'
                                : 'Partly outside this month'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {t.completedAt !== null ? formatDate(t.completedAt, tz) : '—'}
                        </TableCell>
                        <TableCell>{t.siteName ?? '—'}</TableCell>
                        <TableCell>
                          {t.jobType ?? '—'}
                          <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                            {billingMethodLabel(t.billingMethod)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{t.volumeBbl ?? '—'}</TableCell>
                        <TableCell className="text-right">{t.hours ?? '—'}</TableCell>
                        <TableCell className="text-right">{formatNumber(t.extrasCount)}</TableCell>
                        <TableCell className="text-right">
                          {formatUsdFromCents(t.totalCents)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {first.otherCharges.eventCount > 0 && (
                    <TableRow>
                      <TableCell colSpan={7}>
                        Other charges ({formatNumber(first.otherCharges.eventCount)}{' '}
                        {first.otherCharges.eventCount === 1 ? 'line' : 'lines'}, no ticket)
                      </TableCell>
                      <TableCell className="text-right">
                        {formatUsdFromCents(first.otherCharges.totalCents)}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {hasNextPage && (
            <Button
              variant="outline"
              disabled={isFetchingNextPage}
              onClick={(): void => {
                void fetchNextPage();
              }}
              style={{ marginTop: '12px' }}
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more tickets'}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
